// Shared classification for stage-submit failures (apply form, online form, quiz, scheduling).
//
// The bug this replaces: every page gated message extraction on `response.data.error_code`, so a
// 422 that carried `message` but no `error_code` was discarded and the applicant got generic copy.
// That is the shape behind Sentry WS-JOBS-FE-PRODUCTION-BNB. Here the server's message is read
// unconditionally, and the kind is derived from the HTTP status. [MW-2325]
//
// Pure and DOM-free so it can be unit tested and shared across bundles. It is also i18n-free: it
// returns `messageKey`, a natural-language English key the caller passes through its own `t()`.
//
// These bundles run inside the Worker App's webview on old JS engines, so this module sticks to
// widely supported runtime APIs (no Object.entries/values, Array#find or String#includes).

export const SUBMIT_ERROR_KINDS = {
  CANCELED: 'canceled',
  NETWORK: 'network',
  TIMEOUT: 'timeout',
  VALIDATION: 'validation',
  ALREADY_DONE: 'already_done',
  EXPIRED: 'expired',
  RATE_LIMITED: 'rate_limited',
  SERVER: 'server',
  UNKNOWN: 'unknown',
};

// `unknown` and `rate_limited` reuse keys that already exist in the catalogs.
export const SUBMIT_ERROR_MESSAGE_KEYS = {
  canceled: null,
  network: 'We could not reach the server. Please check your connection and try again.',
  timeout: 'This is taking longer than expected. Please try again.',
  validation: 'Please check your answers and try again.',
  already_done: 'You have already completed this step.',
  expired: 'This link is no longer active. Please contact the hiring team.',
  rate_limited: 'Too many requests. Please try again later.',
  server: 'We hit a problem on our end. Please try again.',
  unknown: 'Something went wrong, please try again',
};

const RETRYABLE_KINDS = ['network', 'timeout', 'rate_limited', 'server', 'unknown'];

function isNonEmptyString(value) {
  return typeof value === 'string' && value.replace(/\s+/g, ' ').trim() !== '';
}

// Joins a string or an array of strings, dropping blanks. Returns undefined when nothing is left,
// so the caller falls through to translated copy instead of rendering an empty error -- which is
// what the old `error_code` present / `message` null combination produced.
function normalizeMessage(value) {
  if (isNonEmptyString(value)) return value.trim();

  if (Object.prototype.toString.call(value) === '[object Array]') {
    const parts = [];
    for (let i = 0; i < value.length; i += 1) {
      if (isNonEmptyString(value[i])) parts.push(value[i].trim());
    }
    return parts.length ? parts.join(', ') : undefined;
  }

  return undefined;
}

// Reads whatever the API actually sent, across every shape these endpoints use.
function extractServerMessage(data) {
  // A string body is an HTML error page, not a message. Never render it at the applicant.
  if (!data || typeof data !== 'object') return undefined;

  const direct = normalizeMessage(data.message);
  if (direct) return direct;

  const { errors } = data;
  if (errors) {
    // The refill-form contract: { errors: { messages: [...], missing_required: {...} } }
    const fromMessages = normalizeMessage(errors.messages);
    if (fromMessages) return fromMessages;
    // Some endpoints answer { errors: [...] } or { errors: 'text' }
    const fromErrors = normalizeMessage(errors);
    if (fromErrors) return fromErrors;
  }

  return normalizeMessage(data.error);
}

// Field-level detail for inline highlighting. Names only -- values can echo applicant PII.
function extractFieldErrors(data) {
  if (!data || typeof data !== 'object') return undefined;
  const { errors } = data;
  if (!errors || typeof errors !== 'object') return undefined;
  if (Object.prototype.toString.call(errors) === '[object Array]') return undefined;
  if (errors.missing_required) return errors.missing_required;
  return undefined;
}

function isCanceled(error) {
  if (!error) return false;
  // axios v1 marks both the modern and legacy cancel paths; the legacy marker is axios's own
  // property name, not ours.
  // eslint-disable-next-line no-underscore-dangle
  return error.code === 'ERR_CANCELED' || error.__CANCEL__ === true;
}

function kindForStatus(status) {
  if (status === 408 || status === 429) return SUBMIT_ERROR_KINDS.RATE_LIMITED;
  if (status === 400 || status === 422) return SUBMIT_ERROR_KINDS.VALIDATION;
  if (status === 409) return SUBMIT_ERROR_KINDS.ALREADY_DONE;
  if (status === 401 || status === 403 || status === 404 || status === 410) {
    return SUBMIT_ERROR_KINDS.EXPIRED;
  }
  if (status >= 500 || status === 0) return SUBMIT_ERROR_KINDS.SERVER;
  return SUBMIT_ERROR_KINDS.UNKNOWN;
}

/**
 * Classify an axios failure from a stage submit.
 *
 * @returns {{
 *   kind: string, status: (number|undefined), errorCode: *, serverMessage: (string|undefined),
 *   fieldErrors: *, state: (string|undefined), retryable: boolean, messageKey: (string|null)
 * }}
 */
export function classifySubmitError(error) {
  const response = error && error.response;
  const data = response ? response.data : undefined;
  const status = response ? response.status : undefined;

  let kind;
  if (isCanceled(error)) {
    kind = SUBMIT_ERROR_KINDS.CANCELED;
  } else if (!response) {
    // No response at all: offline, DNS failure, CORS, or an aborted request. Common in a webview
    // on a flaky mobile connection, and previously indistinguishable from a server error.
    kind = error && error.code === 'ECONNABORTED'
      ? SUBMIT_ERROR_KINDS.TIMEOUT
      : SUBMIT_ERROR_KINDS.NETWORK;
  } else {
    kind = kindForStatus(status);
  }

  const isCancel = kind === SUBMIT_ERROR_KINDS.CANCELED;

  return {
    kind,
    status,
    errorCode: data && typeof data === 'object' ? data.error_code : undefined,
    serverMessage: isCancel ? undefined : extractServerMessage(data),
    fieldErrors: extractFieldErrors(data),
    state: data && typeof data === 'object' ? data.state : undefined,
    retryable: RETRYABLE_KINDS.indexOf(kind) !== -1,
    messageKey: SUBMIT_ERROR_MESSAGE_KEYS[kind],
  };
}

/**
 * The text to show the applicant: the server's own message when it sent one, otherwise translated
 * copy for the kind. `translate` is the page's `t`.
 */
export function submitErrorText(classified, translate) {
  if (!classified || classified.kind === SUBMIT_ERROR_KINDS.CANCELED) return '';
  if (classified.serverMessage) return classified.serverMessage;
  const key = classified.messageKey || SUBMIT_ERROR_MESSAGE_KEYS.unknown;
  return typeof translate === 'function' ? translate(key) : key;
}
